import { createSelector } from "@ngrx/store";
import { MAX_ENTITY_STARS } from "globals/helpers/constants";
import { AssetsBaseTableData, AttendeeBaseTableData, BaseTemplateTableData, ExhibitorsBaseTableData, PerformerBaseTableData, ResourcesBaseTableData, SponsorsBaseTableData } from "../../helpers/table-column-types";
import { Asset } from "globals/store/assets.data";
import { Entity } from "globals/store/entity.data";
import { Performer } from "globals/store/performers.data";
import { convertObjectMapToList, sortByProp } from "globals/helpers/helpers";
import { Sponsor } from "globals/store/sponsors.data";
import { Exhibitor } from "globals/store/exhibitors.data";
import { JoiResource } from "globals/store/resources.data";
import { TableLight, TableLights } from "../../helpers/table.data";
import { tableLightColors } from "globals/helpers/table-helpers.data";
import { Dictionary } from "@ngrx/entity";
import { Attendee } from "globals/store/attendees.data";
import { nameJoiner } from "src/app/helpers/helpers";
import { resourcesStateSelector } from "../resources.era";

const performerEntities = (store) => store.performers.entities
const sponsorEntities = (store) => store.sponsors.entities
const exhibitorEntities = (store) => store.exhibitors.entities
const resourceEntities = createSelector(resourcesStateSelector, (state) => state.entities)
const attendees = (store) => store.attendees.entities
const activities = (store) => store.onsite.activities

export const onsiteActivityListReselector = createSelector(
  activities,
  (activities) => Object.values(activities)
)


export const getBaseTemplateTableData = (entity: Entity, WFEmail: string, stars?: number): BaseTemplateTableData => ({
  id: entity.id,
  entity, // two references to the same thing, not copies.  Should be fine
  createdDate: { date: new Date(entity.dateAdded), HHMMText: '' },
  modifiedDate: { date: new Date(entity.dateModified), HHMMText: '' },
  WFEmail: { text: WFEmail },  
  eventName: { text: entity.eventName || '' },
  stars: !stars ? null : { maxStars: MAX_ENTITY_STARS, stars }
})


export const getBaseResourceTableData = (resource: JoiResource): ResourcesBaseTableData => {
  // console.log({ resource })
  const { name, contacts, about, logoUrl, fullLogoUrl, social, primaryContactId, workforceContactEmail, stars } = resource
  const contact = !primaryContactId ? (!contacts.length ? null : contacts[0]) : contacts.find(c => c.id === primaryContactId)

  return {
    ...getBaseTemplateTableData(resource, workforceContactEmail, stars),
    resource,
    name: { text: name },
    description: { text: about },
    logo: !logoUrl ? null : { url: logoUrl, fullUrl: fullLogoUrl, title: `${name}'s logo` },
    social: social || [],
    contactName: { text: nameJoiner(contact) },
    contactEmail: { text: contact?.email || '' },
    contactNumber: { text: contact?.phone || '' },
    WFEmail: { text: workforceContactEmail }
  }
}

export const getBaseExhibitorTableData = (exhibitor: Exhibitor): ExhibitorsBaseTableData => {
  const { name, workforceContactEmail, description, callToAction, callToActionUrl, logoUrl, social, contactName, contactNumber, contactEmail,fullLogoUrl, contactFullAddress, stars } = exhibitor
  return {
    ...getBaseTemplateTableData(exhibitor, workforceContactEmail, stars),
    exhibitor,
    name: { text: name },
    description: { text: description },
    callToActionText: { text: callToAction },
    callToActionUrl: { url: callToActionUrl },
    logo: !logoUrl ? null : { url: logoUrl, fullUrl: fullLogoUrl, title: `${name}'s logo` },
    social: social || [],
    contactName: { text: contactName },
    contactEmail: { text: contactEmail },
    contactNumber: { text: contactNumber},
    address: { text: contactFullAddress }
  }
}

export const getBaseSponsorTableData = (sponsor: Sponsor): SponsorsBaseTableData => {
  const { name, WFContact, description, callToAction, callToActionUrl, logoUrl, fullLogoUrl, social, contactName, contactNumber, contactEmail, contactFullAddress, stars } = sponsor
  return {
    ...getBaseTemplateTableData(sponsor, WFContact, stars),
    sponsor,
    name: { text: name },
    description: { text: description },
    callToActionText: { text: callToAction },
    callToActionUrl: { url: callToActionUrl },
    logo: !logoUrl ? null : { url: logoUrl, fullUrl: fullLogoUrl, title: `${name}'s logo` },
    social: social || [],
    contactName: { text: contactName },
    contactEmail: { text: contactEmail },
    contactNumber: { text: contactNumber},
    WFEmail: { text: WFContact },
    address: { text: contactFullAddress },
  }
}

export const getBaseAttendeeTableData = (attendee: Attendee): AttendeeBaseTableData => {
  const { name, line1, line2, social, contactFullAddress, photoUrl, fullPhotoUrl, WFContact, phone, email } = attendee
  return {
    ...getBaseTemplateTableData(attendee, WFContact),
    attendee,
    name: { name, photoUrl, fullPhotoUrl },
    social: social || [],
    phone: { text: phone},
    line1: { text: line1 },
    line2: { text: line2 },
    email: { text: email },
    photo: !photoUrl ? null : { url: photoUrl, fullUrl: fullPhotoUrl, title: `${name}'s photo` },
    address: { text: contactFullAddress },
  }
}


export const getBasePerformerTableData = (performer: Performer): PerformerBaseTableData => {
  const { name, contact: WFContactEmail, bio, contactName, email, line1, line2, phone, photoUrl, fullPhotoUrl, fullLogoUrl, logoUrl, social, contactFullAddress, stars } = performer
  return {
    ...getBaseTemplateTableData(performer, WFContactEmail, stars),
    performer,
    name: { name, photoUrl, fullPhotoUrl },
    noNestedText: `${name} is not assigned to any sessions`,
    contactName: { text: contactName },
    email: { text: email },
    phone: { text: phone },
    line1: { text: line1 },
    line2: { text: line2 },
    social: social || [],
    bio: { text: bio },
    logo: !logoUrl ? null : { url: logoUrl, fullUrl: fullLogoUrl, title: `${name}'s logo` },
    photo: !photoUrl ? null : { url: photoUrl, fullUrl: fullPhotoUrl, title: `${name}'s photo` },
    address: { text: contactFullAddress },
  }
}

export const getBaseAssetTableData = (asset: Asset): AssetsBaseTableData => {
  const { name, link, description, icon } = asset
  return {
    id: asset.id,
    ...getBaseTemplateTableData(asset, null, 0),
    asset,
    name: { text: name },
    icon: { text: '', icon },
    description: { text: description },
    assetURL: { url: link },
  }
}

export const resourceListReselector = createSelector(
  resourceEntities,
  (resources) => convertObjectMapToList(resources).sort(sortByProp<any, string>('name'))
)

export const exhibitorListReselector = createSelector(
  exhibitorEntities,
  (exhibitors) => convertObjectMapToList(exhibitors).sort(sortByProp<any, string>('name'))
)

export const sponsorListReselector = createSelector(
  sponsorEntities,
  (sponsors) => convertObjectMapToList(sponsors).sort(sortByProp<any, string>('name'))
)

export const performerListReselector = createSelector(
  performerEntities,
  (performers) => convertObjectMapToList(performers).sort(sortByProp<any, string>('name'))
)

export const attendeeListReselector = createSelector(
  attendees,
  (attendees) => convertObjectMapToList(attendees).sort(sortByProp<any, string>('name'))
)

export const addACommaToLights = (lights: TableLight[], index) => { // mutates text in place
  if (lights.length > index + 1) {
    lights[index].text = `${lights[index].text}, `
    addACommaToLights(lights, index+1)
  }
}

const getReadyLights = (ready: number, total: number): TableLights => {
  if (!total) return null
  const percent = ready / total
  const status: TableLights = { percent, lights: [
      !ready ? null : { text: `${ready} ready`, color: tableLightColors.green },
      total - ready === 0 ? null : { text: `${total - ready} not ready`, color: tableLightColors.yellow },
    ].filter(l => l)
  }
  status.lights && addACommaToLights(status.lights, 0)
  return status
}

export const getReadyAssetLights = (assetIds: string[], assetsMap: Dictionary<Asset>): TableLights => {
  const assets = (assetIds || []).map(id => assetsMap[id]).filter(a => !!a)
  return getReadyLights(assets.filter(a => !!a.ready).length, assets.length)
}
